import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Chip, LoadingState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Car, Motorcycle, Plus, Download, Eye, Trash, Clock, CheckCircle, XCircle, UploadSimple, Image, FileText, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function MyVehicles() {
    const { user } = useAuth();
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [open, setOpen] = useState(false);
    const [viewQR, setViewQR] = useState(null);
    const [form, setForm] = useState({
        vehicle_number: "",
        type: "4-wheeler",
        make: "",
        model: "",
        color: "",
        owner_name: user?.name || "",
        flat_id: user?.flat_id || "",
        photo_file_id: null,
        rc_file_id: null,
        rfid_tag_id: ""
    });
    const [photoFile, setPhotoFile] = useState(null);
    const [rcFile, setRcFile] = useState(null);

    async function load() {
        setLoading(true);
        try {
            const response = await api.get("/vehicles");
            setVehicles(response.data);
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, []);

    async function uploadFile(file) {
        const formData = new FormData();
        formData.append("file", file);
        const response = await api.post("/files/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" }
        });
        return response.data.id;
    }

    async function submit(e) {
        e.preventDefault();
        setUploading(true);
        try {
            // Upload files if selected
            let photo_file_id = form.photo_file_id;
            let rc_file_id = form.rc_file_id;

            if (photoFile) {
                toast.info("Uploading vehicle photo...");
                photo_file_id = await uploadFile(photoFile);
            }

            if (rcFile) {
                toast.info("Uploading RC document...");
                rc_file_id = await uploadFile(rcFile);
            }

            // Create vehicle with file IDs
            await api.post("/vehicles", {
                ...form,
                photo_file_id,
                rc_file_id
            });

            toast.success("Vehicle added successfully! Pending admin approval.");
            setOpen(false);
            setForm({
                vehicle_number: "",
                type: "4-wheeler",
                make: "",
                model: "",
                color: "",
                owner_name: user?.name || "",
                flat_id: user?.flat_id || "",
                photo_file_id: null,
                rc_file_id: null,
                rfid_tag_id: ""
            });
            setPhotoFile(null);
            setRcFile(null);
            load();
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setUploading(false);
        }
    }

    async function deleteVehicle(vehicle) {
        if (!confirm(`Delete vehicle ${vehicle.vehicle_number}?`)) return;
        try {
            await api.delete(`/vehicles/${vehicle.id}`);
            toast.success("Vehicle deleted successfully");
            load();
        } catch (e) {
            toast.error(formatError(e));
        }
    }

    function downloadQR(vehicle) {
        if (!vehicle.qr_code_base64) {
            toast.error("QR code not available");
            return;
        }
        const link = document.createElement('a');
        link.href = vehicle.qr_code_base64;
        link.download = `parking-pass-${vehicle.vehicle_number}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Parking pass downloaded!");
    }

    function printSticker(vehicle) {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Vehicle Parking Sticker - ${vehicle.vehicle_number}</title>
                <style>
                    @page { margin: 0; size: 4in 3in; }
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body {
                        font-family: Arial, sans-serif;
                        width: 4in;
                        height: 3in;
                        padding: 0.3in;
                        display: flex;
                        flex-direction: column;
                        justify-content: space-between;
                    }
                    .sticker {
                        border: 3px solid #000;
                        padding: 12px;
                        border-radius: 8px;
                        height: 100%;
                        display: flex;
                        flex-direction: column;
                    }
                    .header {
                        text-align: center;
                        border-bottom: 2px solid #000;
                        padding-bottom: 8px;
                        margin-bottom: 8px;
                    }
                    .society-name {
                        font-size: 18px;
                        font-weight: bold;
                        text-transform: uppercase;
                    }
                    .label {
                        font-size: 11px;
                        color: #666;
                        text-transform: uppercase;
                    }
                    .qr-container {
                        text-align: center;
                        flex: 1;
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                    }
                    .qr-code {
                        width: 140px;
                        height: 140px;
                        margin: 8px 0;
                    }
                    .vehicle-number {
                        font-size: 28px;
                        font-weight: bold;
                        margin: 8px 0;
                        letter-spacing: 2px;
                    }
                    .details {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 6px;
                        margin-top: 8px;
                        font-size: 11px;
                    }
                    .detail-item {
                        padding: 4px;
                        background: #f5f5f5;
                        border-radius: 4px;
                    }
                    .detail-label {
                        font-weight: bold;
                        color: #333;
                    }
                    .pass-number {
                        text-align: center;
                        font-family: 'Courier New', monospace;
                        font-size: 13px;
                        font-weight: bold;
                        padding: 6px;
                        background: #000;
                        color: #fff;
                        border-radius: 4px;
                        margin-top: 8px;
                    }
                    @media print {
                        body { width: 4in; height: 3in; }
                    }
                </style>
            </head>
            <body>
                <div class="sticker">
                    <div class="header">
                        <div class="society-name">${user?.society_name || 'MAINTAYN'}</div>
                        <div class="label">Authorized Parking Pass</div>
                    </div>
                    
                    <div class="qr-container">
                        <img src="${vehicle.qr_code_base64}" class="qr-code" alt="QR Code" />
                        <div class="vehicle-number">${vehicle.vehicle_number}</div>
                    </div>
                    
                    <div class="details">
                        <div class="detail-item">
                            <span class="detail-label">Owner:</span><br>
                            ${vehicle.owner_name}
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Flat:</span><br>
                            ${vehicle.flat ? `${vehicle.flat.block}-${vehicle.flat.number}` : '—'}
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Type:</span><br>
                            ${vehicle.type}
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Slot:</span><br>
                            ${vehicle.parking_slot ? vehicle.parking_slot.slot_number : 'Not Assigned'}
                        </div>
                    </div>
                    
                    <div class="pass-number">${vehicle.parking_pass_number}</div>
                </div>
            </body>
            </html>
        `);
        printWindow.document.close();
        setTimeout(() => {
            printWindow.print();
        }, 250);
    }

    const getStatusChip = (status) => {
        switch (status) {
            case "approved":
                return <Chip color="green" icon={<CheckCircle size={14} />}>Approved</Chip>;
            case "rejected":
                return <Chip color="red" icon={<XCircle size={14} />}>Rejected</Chip>;
            case "pending":
            default:
                return <Chip color="yellow" icon={<Clock size={14} />}>Pending Approval</Chip>;
        }
    };

    if (loading) return <LoadingState />;

    return (
        <div className="animate-fade-up">
            <PageHeader
                overline="Parking"
                title="My Vehicles"
                subtitle={`Manage your registered vehicles and parking passes`}
                actions={
                    <Button onClick={() => setOpen(true)} className="rounded-full" data-testid="my-vehicles-add-btn">
                        <Plus size={16} className="mr-2" />
                        Add Vehicle
                    </Button>
                }
            />

            {vehicles.length === 0 ? (
                <EmptyState
                    icon={<Car size={48} />}
                    title="No Vehicles Registered"
                    description="Add your vehicles to get parking passes and manage parking slots"
                    action={
                        <Button onClick={() => setOpen(true)} className="rounded-full">
                            <Plus size={16} className="mr-2" />
                            Add Your First Vehicle
                        </Button>
                    }
                />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {vehicles.map((vehicle) => (
                        <div key={vehicle.id} data-testid={`my-vehicle-card-${vehicle.vehicle_number}`} className="bg-white border border-brand-line rounded-sm p-6 hover:shadow-md transition-shadow">
                            {/* Header */}
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    {vehicle.type === "2-wheeler" ? (
                                        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                                            <Motorcycle size={24} className="text-blue-600" />
                                        </div>
                                    ) : (
                                        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                                            <Car size={24} className="text-green-600" />
                                        </div>
                                    )}
                                    <div>
                                        <p className="font-heading text-xl font-bold text-brand-ink">
                                            {vehicle.vehicle_number}
                                        </p>
                                        <p className="text-xs text-brand-inkSoft uppercase tracking-wide">
                                            {vehicle.type}
                                        </p>
                                    </div>
                                </div>
                                {getStatusChip(vehicle.status)}
                                <span className="hidden" data-testid={`my-vehicle-status-${vehicle.vehicle_number}`}>{vehicle.status}</span>
                            </div>

                            {/* Details */}
                            <div className="space-y-2 mb-4 pb-4 border-b border-brand-line">
                                <div className="flex justify-between text-sm">
                                    <span className="text-brand-inkSoft">Owner:</span>
                                    <span className="font-medium text-brand-ink">{vehicle.owner_name}</span>
                                </div>
                                {vehicle.make && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-brand-inkSoft">Make/Model:</span>
                                        <span className="font-medium text-brand-ink">{vehicle.make} {vehicle.model}</span>
                                    </div>
                                )}
                                {vehicle.color && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-brand-inkSoft">Color:</span>
                                        <span className="font-medium text-brand-ink">{vehicle.color}</span>
                                    </div>
                                )}
                                {vehicle.parking_slot && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-brand-inkSoft">Parking Slot:</span>
                                        <span className="font-semibold text-green-600">{vehicle.parking_slot.slot_number}</span>
                                    </div>
                                )}
                            </div>

                            {/* Pass Number */}
                            {vehicle.parking_pass_number && (
                                <div className="bg-brand-sage/20 rounded-sm p-3 mb-4">
                                    <p className="text-xs uppercase tracking-wide text-brand-inkSoft mb-1">Parking Pass</p>
                                    <p className="font-mono font-bold text-brand-ink">{vehicle.parking_pass_number}</p>
                                </div>
                            )}

                            {vehicle.rfid_tag_id && (
                                <div className="bg-brand-bg rounded-sm p-3 mb-4" data-testid={`my-vehicle-rfid-${vehicle.vehicle_number}`}>
                                    <p className="text-xs uppercase tracking-wide text-brand-inkSoft mb-1">RFID Tag (Gate Access)</p>
                                    <p className="font-mono font-bold text-brand-ink">{vehicle.rfid_tag_id}</p>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex gap-2">
                                {vehicle.status === "approved" && vehicle.qr_code_base64 && (
                                    <>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setViewQR(vehicle)}
                                            className="flex-1 rounded-full"
                                        >
                                            <Eye size={14} className="mr-1" />
                                            View
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={() => printSticker(vehicle)}
                                            className="flex-1 rounded-full bg-green-600 hover:bg-green-700 text-white"
                                        >
                                            <Printer size={14} className="mr-1" />
                                            Print
                                        </Button>
                                    </>
                                )}
                                {vehicle.status === "pending" && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => deleteVehicle(vehicle)}
                                        className="w-full rounded-full border-red-300 text-red-600 hover:bg-red-50"
                                    >
                                        <Trash size={14} className="mr-1" />
                                        Delete
                                    </Button>
                                )}
                            </div>

                            {vehicle.approval_remarks && vehicle.status === "rejected" && (
                                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-sm">
                                    <p className="text-xs font-medium text-red-800 mb-1">Rejection Reason:</p>
                                    <p className="text-xs text-red-700">{vehicle.approval_remarks}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Add Vehicle Dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Add New Vehicle</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Vehicle Number *</Label>
                                <Input
                                    required
                                    data-testid="my-vehicle-number-input"
                                    placeholder="MH12AB1234"
                                    value={form.vehicle_number}
                                    onChange={(e) => setForm({ ...form, vehicle_number: e.target.value.toUpperCase() })}
                                    className="uppercase"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Vehicle Type *</Label>
                                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                                    <SelectTrigger data-testid="my-vehicle-type-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="2-wheeler">2-Wheeler (Bike/Scooter)</SelectItem>
                                        <SelectItem value="4-wheeler">4-Wheeler (Car/SUV)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Owner Name *</Label>
                            <Input
                                required
                                data-testid="my-vehicle-owner-input"
                                value={form.owner_name}
                                onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label>Make</Label>
                                <Input
                                    placeholder="Honda"
                                    value={form.make}
                                    onChange={(e) => setForm({ ...form, make: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Model</Label>
                                <Input
                                    placeholder="City"
                                    value={form.model}
                                    onChange={(e) => setForm({ ...form, model: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Color</Label>
                                <Input
                                    placeholder="Silver"
                                    value={form.color}
                                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>RFID Tag ID (Optional)</Label>
                            <Input
                                placeholder="e.g. E200400112233"
                                data-testid="my-vehicle-rfid-input"
                                value={form.rfid_tag_id}
                                onChange={(e) => setForm({ ...form, rfid_tag_id: e.target.value.toUpperCase() })}
                                className="font-mono"
                            />
                            <p className="text-xs text-brand-inkSoft">If your society issued you an RFID gate tag, enter its ID here for automatic boom-barrier access once approved.</p>
                        </div>

                        {/* File Uploads */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Vehicle Photo (Optional)</Label>
                                <div className="border-2 border-dashed border-brand-line rounded-sm p-4 text-center">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => setPhotoFile(e.target.files[0])}
                                        className="hidden"
                                        id="photo-upload"
                                    />
                                    <label htmlFor="photo-upload" className="cursor-pointer">
                                        {photoFile ? (
                                            <div className="flex items-center justify-center gap-2 text-green-600">
                                                <Image size={20} />
                                                <span className="text-sm">{photoFile.name}</span>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center gap-2 text-brand-inkSoft">
                                                <UploadSimple size={32} />
                                                <span className="text-sm">Click to upload photo</span>
                                                <span className="text-xs">PNG, JPG up to 5MB</span>
                                            </div>
                                        )}
                                    </label>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>RC Document (Optional)</Label>
                                <div className="border-2 border-dashed border-brand-line rounded-sm p-4 text-center">
                                    <input
                                        type="file"
                                        accept="image/*,.pdf"
                                        onChange={(e) => setRcFile(e.target.files[0])}
                                        className="hidden"
                                        id="rc-upload"
                                    />
                                    <label htmlFor="rc-upload" className="cursor-pointer">
                                        {rcFile ? (
                                            <div className="flex items-center justify-center gap-2 text-green-600">
                                                <FileText size={20} />
                                                <span className="text-sm">{rcFile.name}</span>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center gap-2 text-brand-inkSoft">
                                                <UploadSimple size={32} />
                                                <span className="text-sm">Click to upload RC</span>
                                                <span className="text-xs">PNG, JPG, PDF up to 5MB</span>
                                            </div>
                                        )}
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-4">
                            <p className="text-sm text-yellow-800">
                                <strong>Note:</strong> Your vehicle will be reviewed by the admin/committee before approval. 
                                Once approved, you'll receive a QR code parking pass.
                            </p>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={uploading}>
                                Cancel
                            </Button>
                            <Button 
                                type="submit" 
                                data-testid="my-vehicle-submit-btn"
                                className="bg-brand-action hover:bg-brand-actionHover text-white"
                                disabled={uploading}
                            >
                                {uploading ? "Uploading..." : "Add Vehicle"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* View QR Dialog */}
            <Dialog open={!!viewQR} onOpenChange={() => setViewQR(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Parking Pass - {viewQR?.vehicle_number}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="bg-white p-6 border-2 border-brand-line rounded-sm">
                            {viewQR?.qr_code_base64 && (
                                <img src={viewQR.qr_code_base64} alt="QR Code" className="w-full max-w-sm mx-auto" />
                            )}
                        </div>
                        <div className="bg-brand-sage/20 p-4 rounded-sm text-center">
                            <p className="text-sm text-brand-inkSoft mb-1">Parking Pass Number</p>
                            <p className="text-xl font-mono font-bold text-brand-ink">{viewQR?.parking_pass_number}</p>
                        </div>
                        <p className="text-sm text-brand-inkSoft text-center">
                            Show this QR code to security for entry/exit verification
                        </p>
                        <Button
                            onClick={() => downloadQR(viewQR)}
                            className="w-full rounded-full bg-brand-action hover:bg-brand-actionHover text-white"
                        >
                            <Download size={16} className="mr-2" />
                            Download Parking Pass
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
